import React, { useState } from 'react';
import { Checkbox, Input, Button, Tooltip, Popconfirm, Space, Tag, DatePicker } from 'antd';
import { DeleteOutlined, EditOutlined, CheckOutlined, CloseOutlined, CalendarOutlined } from '@ant-design/icons';
import { WeeklyTask, TaskPriority } from '../../ProfitScenario/types';
import dayjs, { Dayjs } from 'dayjs';

interface TaskItemProps {
    task: WeeklyTask;
    editingTaskId: string | null;
    editingContent: string;
    priorityConfig: Record<TaskPriority, { color: string; label: string }>;
    onToggleTask: (taskId: string) => void;
    handleStartEdit: (task: WeeklyTask) => void;
    setEditingContent: (content: string) => void;
    handleSaveEdit: () => void;
    handleCancelEdit: () => void;
    onDeleteTask: (taskId: string) => void;
    onUpdateTask: (taskId: string, updates: any) => void;
}

export const TaskItem: React.FC<TaskItemProps> = ({
    task,
    editingTaskId,
    editingContent,
    priorityConfig,
    onToggleTask,
    handleStartEdit,
    setEditingContent,
    handleSaveEdit,
    handleCancelEdit,
    onDeleteTask,
    onUpdateTask
}) => {
    const [editingDate, setEditingDate] = useState(false);
    const [tempStartDate, setTempStartDate] = useState(task.startDate || dayjs().format('YYYY-MM-DD'));
    const [tempEndDate, setTempEndDate] = useState(task.endDate || dayjs().add(1, 'day').format('YYYY-MM-DD'));

    const dateDisplay = `${task.startDate || dayjs().format('MM-DD')} ~ ${task.endDate || dayjs().add(1, 'day').format('MM-DD')}`;

    const handleDateEdit = () => {
        setTempStartDate(task.startDate || dayjs().format('YYYY-MM-DD'));
        setTempEndDate(task.endDate || dayjs().add(1, 'day').format('YYYY-MM-DD'));
        setEditingDate(true);
    };

    const handleDateSave = () => {
        if (tempStartDate && tempEndDate) {
            onUpdateTask(task.id, {
                startDate: tempStartDate,
                endDate: tempEndDate
            });
            setEditingDate(false);
        }
    };

    const handleDateCancel = () => {
        setTempStartDate(task.startDate || dayjs().format('YYYY-MM-DD'));
        setTempEndDate(task.endDate || dayjs().add(1, 'day').format('YYYY-MM-DD'));
        setEditingDate(false);
    };

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 8px',
                opacity: task.completed ? 0.6 : 1,
                background: task.completed ? '#f5f5f5' : 'white',
                borderRadius: 4,
                border: '1px solid #e8e8e8',
                transition: 'all 0.2s'
            }}
        >
            {/* Checkbox */}
            <Checkbox
                checked={task.completed}
                onChange={() => onToggleTask(task.id)}
            />

            {/* 优先级标签 */}
            <Tag
                color={priorityConfig[task.priority].color}
                style={{ margin: 0, fontSize: 10, padding: '0 4px', minWidth: 24, textAlign: 'center' }}
            >
                {priorityConfig[task.priority].label}
            </Tag>

            {/* 任务内容 */}
            {editingTaskId === task.id ? (
                <Input
                    size="small"
                    value={editingContent}
                    onChange={e => setEditingContent(e.target.value)}
                    onPressEnter={handleSaveEdit}
                    autoFocus
                    style={{ flex: 1 }}
                    suffix={
                        <Space size={2}>
                            <Button
                                type="text"
                                size="small"
                                icon={<CheckOutlined />}
                                onClick={handleSaveEdit}
                                style={{ padding: '0 4px', height: 20 }}
                            />
                            <Button
                                type="text"
                                size="small"
                                icon={<CloseOutlined />}
                                onClick={handleCancelEdit}
                                style={{ padding: '0 4px', height: 20 }}
                            />
                        </Space>
                    }
                />
            ) : (
                <span
                    style={{
                        flex: 1,
                        fontSize: 13,
                        textDecoration: task.completed ? 'line-through' : 'none',
                        color: task.completed ? '#999' : '#333',
                        cursor: 'pointer'
                    }}
                    onDoubleClick={() => handleStartEdit(task)}
                >
                    {task.content}
                </span>
            )}

            {/* 日期信息 */}
            {editingDate ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 220 }}>
                    <DatePicker
                        size="small"
                        format="MM-DD"
                        value={dayjs(tempStartDate)}
                        onChange={(date: Dayjs | null) => {
                            if (date) setTempStartDate(date.format('YYYY-MM-DD'));
                        }}
                        style={{ width: 90 }}
                    />
                    <span style={{ fontSize: 11 }}>~</span>
                    <DatePicker
                        size="small"
                        format="MM-DD"
                        value={dayjs(tempEndDate)}
                        onChange={(date: Dayjs | null) => {
                            if (date) setTempEndDate(date.format('YYYY-MM-DD'));
                        }}
                        style={{ width: 90 }}
                    />
                    <Space size={2}>
                        <Button
                            type="text"
                            size="small"
                            icon={<CheckOutlined />}
                            onClick={handleDateSave}
                            style={{ padding: '0 2px', height: 20 }}
                        />
                        <Button
                            type="text"
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={handleDateCancel}
                            style={{ padding: '0 2px', height: 20 }}
                        />
                    </Space>
                </div>
            ) : (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        minWidth: 110,
                        cursor: 'pointer',
                        padding: '2px 8px',
                        borderRadius: 3,
                        background: '#f5f5f5',
                        border: '1px solid #d9d9d9'
                    }}
                    onClick={handleDateEdit}
                    title="点击编辑日期"
                >
                    <CalendarOutlined style={{ fontSize: 11, color: '#666' }} />
                    <span style={{ fontSize: 11, color: '#666' }}>
                        {dateDisplay}
                    </span>
                </div>
            )}

            {/* 操作按钮 */}
            {editingTaskId !== task.id && !editingDate && (
                <Space size={4}>
                    <Tooltip title="编辑">
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleStartEdit(task)}
                            style={{ padding: '0 4px', height: 20, fontSize: 12 }}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="确定删除此任务?"
                        onConfirm={() => onDeleteTask(task.id)}
                        okText="删除"
                        cancelText="取消"
                    >
                        <Tooltip title="删除">
                            <Button
                                type="text"
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                                style={{ padding: '0 4px', height: 20, fontSize: 12 }}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )}
        </div>
    );
};
